// Custom adapter to convert Elasticsearch responses to Instantsearch format
// This allows Searchkit 4.x / Instantsearch to work with existing Rails endpoints

export const createElasticsearchAdapter = (endpointUrl, options = {}) => {
  const { httpHeaders = {}, timeout = 20000 } = options;

  return {
    search: async (requests) => {
      try {
        // Use the first request for main query params
        const request = requests[0];
        const { indexName, params } = request;
        
        // Consolidate refinementList from all requests
        // InstantSearch may send separate requests for each refinement list
        const consolidatedRefinementList = {};
        requests.forEach((req) => {
          if (req.params?.refinementList) {
            Object.assign(consolidatedRefinementList, req.params.refinementList);
          }
        });

        // Build Elasticsearch query from Instantsearch params
        // CRITICAL FIX: Based on user feedback:
        // - Clicking page 1 -> from=100 (should be from=0) -> params.page is 1 when it should be 0
        // - Clicking page 2 -> from=200 (should be from=100) -> params.page is 2 when it should be 1
        // 
        // This means params.page is arriving as 1-based (1, 2, 3...) when it should be 0-based (0, 1, 2...)
        // OR params.page is arriving correctly but we're not converting it properly
        //
        // Solution: Always subtract 1 from params.page to convert from 1-based to 0-based
        // If params.page=1 (page 1), subtract 1 -> page=0 -> from=0 ✓
        // If params.page=2 (page 2), subtract 1 -> page=1 -> from=100 ✓
        const hitsPerPage = params.hitsPerPage || 100;
        
        // CRITICAL FIX: Based on logs, when clicking page 2:
        // - refine(1) is called (correct, 0-based)
        // - params.page arrives as 1 (correct, 0-based)
        // - But we were converting 1 to 0, which is WRONG
        //
        // The issue: InstantSearch's usePagination passes page as 0-based to the adapter
        // We should NOT convert it, just use it directly
        //
        // SOLUTION: Use params.page directly as 0-based (no conversion needed)
        let page = params.page !== undefined ? params.page : 0;
        
        // Calculate from offset (0-based)
        const from = page * hitsPerPage;
        
        // Collect all filters first (will be added to query.filter to affect aggregations)
        const queryFilters = [];
        
        // Determine sort order
        // Priority: customSort > params.sort > default
        // If there's a query and custom sort is not _score, combine _score with custom sort
        const hasQuery = params.query && params.query.trim();
        let sortOrder;
        
        if (params.customSort) {
          // Custom sort specified
          const sortField = params.customSort.field;
          const sortDirection = params.customSort.order || "desc";
          
          // If there's a query and sort is not by _score, combine _score with custom sort
          // This ensures relevance is considered when searching
          if (hasQuery && sortField !== "_score") {
            // Sort by relevance first, then by custom field
            sortOrder = [
              { _score: { order: "desc" } },
              { [sortField]: { order: sortDirection } }
            ];
          } else {
            // No query or sorting by _score, use custom sort directly
            sortOrder = [{ [sortField]: { order: sortDirection } }];
          }
        } else if (params.sort && params.sort.length > 0) {
          // Use sort from params (from InstantSearch)
          sortOrder = params.sort;
        } else if (hasQuery) {
          // If there's a query, sort by relevance (_score) first
          sortOrder = [{ _score: { order: "desc" } }];
        } else {
          // Default: sort by _score desc (relevance)
          // Don't use created_at as default since not all indices have it
          sortOrder = [{ _score: { order: "desc" } }];
        }
        
        let esQuery = {
          size: hitsPerPage,
          from: from,
          sort: sortOrder,
          aggs: {}
        };

        // Convert Instantsearch filters to Elasticsearch query filters
        // IMPORTANT: Filters go in query.filter (not post_filter) so they affect aggregations
        // This allows aggregations to update when filters change (faceted search behavior)
        
        if (params.filters && params.filters.length > 0) {
          params.filters.forEach(filter => {
            if (typeof filter === 'string') {
              // Format: "field:value"
              const [field, value] = filter.split(':');
              if (field && value) {
                queryFilters.push({ term: { [field]: value } });
              }
            } else if (typeof filter === 'object') {
              // Already in ES format
              queryFilters.push(filter);
            }
          });
        }
        
        // Handle facetFilters - InstantSearch passes refinements as facetFilters
        // Format: [["attribute:value"], ["attribute2:value2"]] or ["attribute:value"]
        // This is how InstantSearch passes selected refinement values
        // Priority: facetFilters > refinementList (facetFilters is the source of truth)
        
        // Check if facetFilters exists (even if empty array, it means InstantSearch is managing filters)
        const hasFacetFilters = params.hasOwnProperty('facetFilters');
        
        if (hasFacetFilters && params.facetFilters && params.facetFilters.length > 0) {
          // Group filters by attribute
          const filtersByAttribute = {};
          
          // Handle different formats:
          // Format 1: [["attribute:value"], ["attribute2:value2"]] - array of arrays
          // Format 2: ["attribute:value", "attribute2:value2"] - flat array
          // Format 3: [["attribute:value", "attribute2:value2"]] - single array with multiple values
          
          params.facetFilters.forEach((filterGroup) => {
            // filterGroup can be a string or an array of strings
            const filterArray = Array.isArray(filterGroup) ? filterGroup : [filterGroup];
            
            filterArray.forEach((filter) => {
              if (typeof filter === 'string') {
                // Format: "attribute:value"
                const [field, value] = filter.split(':');
                if (field && value) {
                  if (!filtersByAttribute[field]) {
                    filtersByAttribute[field] = [];
                  }
                  // Avoid duplicates
                  if (!filtersByAttribute[field].includes(value)) {
                    filtersByAttribute[field].push(value);
                  }
                }
              }
            });
          });
          
          // Convert grouped filters to Elasticsearch filters
          Object.keys(filtersByAttribute).forEach(attr => {
            const values = filtersByAttribute[attr];
            if (values.length === 1) {
              queryFilters.push({ term: { [attr]: values[0] } });
            } else {
              // Multiple values = OR condition (should match any of them)
              queryFilters.push({
                bool: {
                  should: values.map(val => ({ term: { [attr]: val } })),
                  minimum_should_match: 1
                }
              });
            }
          });
        } else if (!hasFacetFilters) {
          // Only process refinementList if facetFilters is not present at all (fallback)
          // If facetFilters exists but is empty, don't process refinementList (user cleared filters)
          const refinementListToUse = Object.keys(consolidatedRefinementList).length > 0 
            ? consolidatedRefinementList 
            : params.refinementList;
            
          if (refinementListToUse && Object.keys(refinementListToUse).length > 0) {
            Object.keys(refinementListToUse).forEach(attr => {
              const values = refinementListToUse[attr];
              if (Array.isArray(values) && values.length > 0) {
                if (values.length === 1) {
                  queryFilters.push({ term: { [attr]: values[0] } });
                } else {
                  queryFilters.push({
                    bool: {
                      should: values.map(val => ({ term: { [attr]: val } })),
                      minimum_should_match: 1
                    }
                  });
                }
              }
            });
          }
        }
        
        // Build query with filters - filters go in query.filter to affect aggregations
        // Handle query - convert Instantsearch query to Elasticsearch format
        // Build base query
        let baseQuery;
        if (params.query && params.query.trim()) {
          // If queryFields are provided, use multi_match, otherwise use query_string
          if (params.queryFields && params.queryFields.length > 0) {
            // Use multi_match with phrase_prefix for better partial matching
            // This allows matching partial words at the beginning
            baseQuery = {
              bool: {
                should: [
                  {
                    multi_match: {
                      query: params.query.trim(),
                      fields: params.queryFields,
                      type: "best_fields",
                      operator: "or"
                    }
                  },
                  {
                    multi_match: {
                      query: params.query.trim(),
                      fields: params.queryFields,
                      type: "phrase_prefix",
                      operator: "or"
                    }
                  }
                ],
                minimum_should_match: 1
              }
            };
          } else {
            baseQuery = {
              query_string: {
                query: params.query,
                default_operator: "OR"
              }
            };
          }
        } else {
          baseQuery = { match_all: {} };
        }
        
        // Build final query - keep base query simple for backend compatibility
        esQuery.query = baseQuery;
        
        // Add filters to post_filter for backend compatibility
        // NOTE: post_filter doesn't affect aggregations, but it's what the backend expects
        // We'll handle aggregations separately using filtered aggregations
        if (queryFilters.length > 0) {
          esQuery.post_filter = {
            bool: {
              must: queryFilters
            }
          };
        }

        // Convert facets to aggregations with filtering
        // For faceted search, each aggregation should exclude its own filters but include others
        if (params.facets && params.facets.length > 0) {
          params.facets.forEach(facetName => {
            // Get filters for OTHER attributes (exclude filters for this facet)
            const otherFilters = [];
            
            queryFilters.forEach(filter => {
              // Parse filter to see which attribute it's for
              // For term filters: { term: { "attribute": "value" } }
              // For bool.should filters: { bool: { should: [{ term: { "attribute": "value" } }] } }
              let filterAttribute = null;
              
              if (filter.term) {
                // Simple term filter
                filterAttribute = Object.keys(filter.term)[0];
              } else if (filter.bool && filter.bool.should) {
                // Bool should filter - check first term
                const firstShould = filter.bool.should[0];
                if (firstShould && firstShould.term) {
                  filterAttribute = Object.keys(firstShould.term)[0];
                }
              }
              
              // Only include filter if it's for a different attribute
              if (filterAttribute && filterAttribute !== facetName) {
                otherFilters.push(filter);
              }
            });
            
            if (otherFilters.length > 0) {
              // Use filtered aggregation - this makes aggregations update when other filters change
              esQuery.aggs[facetName] = {
                filter: {
                  bool: {
                    must: otherFilters
                  }
                },
                aggs: {
                  [facetName]: {
                    terms: {
                      field: facetName,
                      size: 100
                    }
                  }
                }
              };
            } else {
              // No other filters, use simple aggregation
              esQuery.aggs[facetName] = {
                terms: {
                  field: facetName,
                  size: 100
                }
              };
            }
          });
        }
        
        // Also handle refinementList attributes
        if (params.refinementList && Object.keys(params.refinementList).length > 0) {
          Object.keys(params.refinementList).forEach(attr => {
            esQuery.aggs[attr] = {
              terms: {
                field: attr,
                size: 100
              }
            };
          });
        }

        // Add default filters if needed - add to query.filter (affects aggregations)
        if (params.defaultFilters && params.defaultFilters.length > 0) {
          // Ensure query is wrapped in bool
          if (!esQuery.query.bool) {
            esQuery.query = {
              bool: {
                must: [esQuery.query],
                filter: []
              }
            };
          }
          if (!esQuery.query.bool.filter) {
            esQuery.query.bool.filter = [];
          }
          // Add default filters to query.filter
          esQuery.query.bool.filter.push(...params.defaultFilters);
        }
        
        const response = await fetch(endpointUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': window.Hupe8?.token || '',
            ...httpHeaders
          },
          body: JSON.stringify(esQuery)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Search request failed: ${response.statusText} - ${errorText}`);
        }

        const esResponse = await response.json();

        // Convert Elasticsearch response to Instantsearch format
        // InstantSearch expects hits with data at root level, but we preserve _source for compatibility
        const hits = (esResponse.hits?.hits || []).map(hit => {
          const source = hit._source || {};
          return {
            objectID: hit._id,
            _id: hit._id,
            _source: source, // Keep _source for compatibility with existing components
            ...source // Spread source data at root level for InstantSearch
          };
        });

        const total = esResponse.hits?.total?.value ?? esResponse.hits?.total ?? 0;

        // Convert aggregations to facets
        // Handle both simple aggregations and filtered aggregations (nested structure)
        const facets = {};
        if (esResponse.aggregations) {
          Object.keys(esResponse.aggregations).forEach(key => {
            const agg = esResponse.aggregations[key];
            
            // Check if this is a filtered aggregation
            // Filtered aggregations have structure: { doc_count: X, [key]: { buckets: [...] } }
            // Simple aggregations have structure: { buckets: [...] }
            if (agg[key] && agg[key].buckets) {
              // Filtered aggregation: buckets are nested inside agg[key]
              facets[key] = {};
              agg[key].buckets.forEach(bucket => {
                facets[key][bucket.key] = bucket.doc_count;
              });
            } else if (agg.aggs && agg.aggs[key] && agg.aggs[key].buckets) {
              // Alternative filtered aggregation structure: buckets are nested inside aggs[key]
              facets[key] = {};
              agg.aggs[key].buckets.forEach(bucket => {
                facets[key][bucket.key] = bucket.doc_count;
              });
            } else if (agg.buckets) {
              // Simple aggregation: buckets are directly in the aggregation
              facets[key] = {};
              agg.buckets.forEach(bucket => {
                facets[key][bucket.key] = bucket.doc_count;
              });
            }
          });
        }

        // InstantSearch expects page to be 0-based in the response
        const result = {
          results: [{
            hits,
            nbHits: total,
            page: page,
            nbPages: Math.ceil(total / hitsPerPage),
            hitsPerPage,
            facets,
            processingTimeMS: esResponse.took || 0,
            query: params.query || '',
            exhaustiveNbHits: false
          }]
        };
        
        return result;
      } catch (error) {
        console.error('Search error:', error);
        // Return empty result on error
        const requestParams = requests?.[0]?.params || {};
        return {
          results: [{
            hits: [],
            nbHits: 0,
            page: 0,
            nbPages: 0,
            hitsPerPage: requestParams.hitsPerPage || 100,
            facets: {},
            processingTimeMS: 0,
            query: requestParams.query || '',
            exhaustiveNbHits: false
          }]
        };
      }
    },
    searchForFacetValues: async (requests) => {
      // Not implemented yet, but required by Instantsearch
      return { facetHits: [] };
    }
  };
};

